import * as moment from 'moment';
import { Observable } from 'rxjs';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { AnnualMinimumDetails } from '../../../../../models/transactions/annual-minimum-details';
import { LumpSumPaymentInstruction } from '../../../../../models/transactions/lump-sum-payment-instruction';
import { WithdrawalInstruction } from '../../../../../models/submissions/withdrawal-instruction';
import { RecurringPaymentInstruction } from '../../../../../models/submissions/recurring-payment-instruction';
import { FundType } from '../../../../../models/transactions/fund-type';
import { VariableBenefitAccountDetails } from '../../../../../models/transactions/variable-benefit-account-details';
import { VbPaymentChangeRecurringPaymentFormGroup } from '../../../../../models/shared/forms/vb-payment-change-recurring-payment-form-group';
import { VbPaymentChangePaymentStepFormGroup } from '../../../../../models/shared/forms/vb-payment-change-payment-step-form-group';
import { VbPaymentChangeDepositsStepFormGroup } from '../../../../../models/shared/forms/vb-payment-change-deposits-step-form-group';
import { VbWithdrawalStepFormGroup } from '../../../../../models/shared/forms/vb-withdrawal-step-form-group';
import { VbPaymentChangeMinAgeFormGroup } from '../../../../../models/shared/forms/vb-payment-change-min-age-form-group';
import {
  CreateVariableBenefitPaymentChangeRequest,
  LumpSumPaymentInstructionChangeRequest,
  RecurringPaymentInstructionChangeRequest
} from '../../../../../models/users/requests/submissionRequests/create-variable-benefit-payment-change-request';
import { CreateWithdrawalInstructionRequest } from '../../../../../models/submissions/requests/create-withdrawal-instruction-request';
import { RecurringPaymentAmountTypeEnum } from '../../../../../models/enum/shared/recurring-payment-amount-type.enum';
import { PaymentMethodTypeEnum } from '../../../../../models/enum/shared/payment-method-type-enum';
import { BankAccount } from '../../../../banking/models/bank-account';
import { VariableBenefitPaymentFrequencyEnum } from '../../../../../models/enum/shared/variable-benefit-payment-frequency.enum';
import { Spouse } from '../../../../../models/users/dto/spouse';
import { VbMinimumAgeUtils } from '../../../../../utils/vb-minimum-age-utils';
import { MemberVbAccountEstimatedAnnualPaymentTotal } from '../../../../../models/transactions/member-vb-account-estimated-annual-payment-total';
import { VbPaymentChangePaymentsStepUtils } from './vb-payment-change-payments-step.utils';

export interface VbPaymentChangeReviewMemberInfo {
  fullName: string;
  spouseFirstName: string | null | undefined;
  spouseLastName: string | null | undefined;
  memberBirthDate: string;
  activeSpouse: Spouse | null;
}

export enum VbScheduledChangeOutcomeEnum {
  Cancels = 'Cancels',
  Replaces = 'Replaces'
}

export interface VbScheduledChangeNotice {
  outcome: VbScheduledChangeOutcomeEnum;
  scheduledDate: string;
}

export class VbPaymentChangeReviewStepUtils {
  static buildRecurringPaymentInstructionFromForm(
    form: VbPaymentChangeRecurringPaymentFormGroup
  ): RecurringPaymentInstruction {
    const instruction = new RecurringPaymentInstruction();
    instruction.paymentFrequencyId = form.controls.paymentFrequencyId.value;
    instruction.paymentAmount = form.controls.paymentAmount.value ?? 0;
    instruction.useMinimumAmount =
      form.controls.recurringPaymentAmountType.value === RecurringPaymentAmountTypeEnum.Minimum;
    const month = String(form.controls.paymentStartMonth.value).padStart(2, '0');
    instruction.paymentStartDate = `${form.controls.paymentStartYear.value}-${month}-01`;
    return instruction;
  }

  /**
   * Derives a RecurringPaymentInstruction for display from the request's change field.
   * Returns previousScheduled unchanged when no change is present in the request.
   * When a change is present (including a removal), converts the change request to a
   * RecurringPaymentInstruction so the card can detect removal via amount/useMinimum comparison.
   */
  static newScheduledInstructionFromRequest(
    change: RecurringPaymentInstructionChangeRequest | null,
    previousScheduled: RecurringPaymentInstruction | null | undefined,
    frequency: VariableBenefitPaymentFrequencyEnum
  ): RecurringPaymentInstruction | null {
    if (!change) return previousScheduled ?? null;

    const instruction = new RecurringPaymentInstruction();
    instruction.paymentFrequencyId = frequency;
    instruction.paymentStartDate = change.paymentStartDate;
    instruction.paymentAmount = change.paymentAmount;
    instruction.useMinimumAmount = change.useMinimumAmount;
    return instruction;
  }

  static sameRecurringAmount(
    candidate: Pick<RecurringPaymentInstruction, 'paymentAmount' | 'useMinimumAmount'>,
    instruction: RecurringPaymentInstruction
  ): boolean {
    return instruction.useMinimumAmount
      ? candidate.useMinimumAmount
      : !candidate.useMinimumAmount && candidate.paymentAmount === instruction.paymentAmount;
  }

  static scheduledRecurringChangeNotice$(
    request$: Observable<CreateVariableBenefitPaymentChangeRequest>,
    currentForgoRecurring$: Observable<boolean>,
    active$: Observable<RecurringPaymentInstruction | null | undefined>,
    scheduled$: Observable<RecurringPaymentInstruction | null | undefined>,
    frequency: VariableBenefitPaymentFrequencyEnum
  ): Observable<VbScheduledChangeNotice | null> {
    return combineLatest([request$, currentForgoRecurring$, active$, scheduled$]).pipe(
      map(([req, currentForgo, active, scheduled]) =>
        VbPaymentChangeReviewStepUtils.scheduledRecurringChangeNotice(
          frequency === VariableBenefitPaymentFrequencyEnum.Monthly
            ? req.monthlyRecurringPaymentInstructionChange
            : req.annualRecurringPaymentInstructionChange,
          active,
          scheduled,
          req.forgoRecurringPayments && !currentForgo
        )
      )
    );
  }

  static scheduledLumpSumNotice$(
    request$: Observable<CreateVariableBenefitPaymentChangeRequest>,
    currentForgoRecurring$: Observable<boolean>,
    waiting$: Observable<LumpSumPaymentInstruction | null | undefined>
  ): Observable<VbScheduledChangeNotice | null> {
    return combineLatest([request$, currentForgoRecurring$, waiting$]).pipe(
      map(([req, currentForgo, waiting]) =>
        VbPaymentChangeReviewStepUtils.scheduledLumpSumNotice(
          req.lumpSumPaymentInstructionChange,
          waiting,
          req.forgoRecurringPayments && !currentForgo
        )
      )
    );
  }

  static newAnnualMinimumDetails$(
    memberVbAccountDetails$: Observable<VariableBenefitAccountDetails>,
    member$: Observable<VbPaymentChangeReviewMemberInfo>,
    getMinAgeForm: () => VbPaymentChangeMinAgeFormGroup | null
  ): Observable<AnnualMinimumDetails> {
    return combineLatest([memberVbAccountDetails$, member$]).pipe(
      map(([details, member]) => {
        const minAgeForm = getMinAgeForm();
        if (minAgeForm?.dirty) {
          const spouseId = minAgeForm.controls.minimumAgeSpouseId.value;
          const dob = minAgeForm.controls.minimumAgeDateOfBirth.value;
          const isSpouse = spouseId !== null;

          const newDetails = new AnnualMinimumDetails();
          newDetails.minimumAgeSpouseId = spouseId;
          newDetails.minimumAgeDateOfBirth = dob ?? '';
          newDetails.minimumAgeName = isSpouse ? `${member.spouseFirstName} ${member.spouseLastName}` : member.fullName;
          newDetails.effectiveYear = VbMinimumAgeUtils.minimumAgeChangeEffectiveYear(details);
          newDetails.onDeserialize();
          return newDetails;
        }
        const elected = details.nextYearAnnualMinimumDetails ?? details.currentYearAnnualMinimumDetails;
        const resolvedDateOfBirth =
          VbMinimumAgeUtils.resolveMinimumAgeDateOfBirth(
            elected.minimumAgeSpouseId ?? null,
            member.memberBirthDate,
            member.activeSpouse
          ) ?? elected.minimumAgeDateOfBirth?.split('T')[0];
        return window?.injector?.Deserialize?.instanceOf(AnnualMinimumDetails, {
          ...elected,
          minimumAgeDateOfBirth: resolvedDateOfBirth ?? '',
          formattedDateOfBirth: ''
        });
      })
    );
  }

  static newLumpSumPaymentInstruction$(
    paymentStepForm$: Observable<VbPaymentChangePaymentStepFormGroup>,
    lumpSumRemoved$: Observable<boolean>,
    currentLumpSumPaymentInstruction$: Observable<LumpSumPaymentInstruction | null>
  ): Observable<LumpSumPaymentInstruction | null> {
    return combineLatest([paymentStepForm$, lumpSumRemoved$, currentLumpSumPaymentInstruction$]).pipe(
      map(([form, lumpSumRemoved, currentInstruction]) => {
        if (lumpSumRemoved && currentInstruction) {
          const instruction = new LumpSumPaymentInstruction();
          instruction.paymentAmount = 0;
          instruction.desiredPaymentDate = currentInstruction.desiredPaymentDate;
          instruction.onDeserialize();
          return instruction;
        }

        if (!form.dirty) return null;

        const amount = form.controls.lumpSumPaymentAmount.value;
        const month = form.controls.lumpSumPaymentMonth.value;
        const year = form.controls.lumpSumPaymentYear.value;

        if (amount === null || month === null || year === null) return null;

        const instruction = new LumpSumPaymentInstruction();
        instruction.paymentAmount = amount;
        instruction.desiredPaymentDate = `${year}-${String(month).padStart(2, '0')}-01`;
        instruction.onDeserialize();
        return instruction;
      })
    );
  }

  static newForgoRecurringPayments$(
    paymentStepForm$: Observable<VbPaymentChangePaymentStepFormGroup>
  ): Observable<boolean> {
    return paymentStepForm$.pipe(map(form => form.controls.forgoRecurringPayments.value));
  }

  static newDepositBankAccount$(
    depositsStepForm$: Observable<VbPaymentChangeDepositsStepFormGroup>,
    bankAccounts$: Observable<BankAccount[]>,
    currentDepositBankAccount$: Observable<BankAccount | null | undefined>
  ): Observable<BankAccount | null | undefined> {
    return combineLatest([depositsStepForm$, bankAccounts$, currentDepositBankAccount$]).pipe(
      map(([form, bankAccounts, currentAccount]) => {
        const selectedId = form.controls.bankAccountId.value;
        if (selectedId === null) return null;
        return bankAccounts.find(ba => ba.id === selectedId) ?? currentAccount;
      })
    );
  }

  static newWithdrawalInstructions$(
    withdrawalStepForm$: Observable<VbWithdrawalStepFormGroup>,
    fundTypes$: Observable<FundType[]>,
    currentWithdrawalInstructions$: Observable<WithdrawalInstruction[]>
  ): Observable<WithdrawalInstruction[]> {
    return combineLatest([withdrawalStepForm$, fundTypes$, currentWithdrawalInstructions$]).pipe(
      map(([form, fundTypes, currentInstructions]) => {
        if (!form.dirty) return currentInstructions;

        return form.controls.instructions.controls.map(fg => {
          const instruction = new WithdrawalInstruction();
          instruction.fundId = fg.controls.fund.value;
          instruction.percentage = fg.controls.percentage.value ?? 0;
          instruction.fundBalance = fg.controls.balance.value;
          const matchingFundType = fundTypes.find(ft => ft.id === instruction.fundId);
          if (matchingFundType) {
            instruction.fund = matchingFundType;
          }
          return instruction;
        });
      })
    );
  }

  /**
   * Computes the new active recurring payment instruction based on the previous active and
   * the new scheduled instruction derived from the request.
   *
   * A removal is detected when newScheduled matches previousActive (see sameRecurringAmount) —
   * in that case the active payment is returned unchanged (no endDate).
   * Otherwise, the active is cloned with an endDate set to one period before the new scheduled starts.
   */
  static newActiveRecurringPaymentInstruction$(
    previousActive$: Observable<RecurringPaymentInstruction | null | undefined>,
    newScheduled$: Observable<RecurringPaymentInstruction | null | undefined>,
    frequency: 'monthly' | 'annual'
  ): Observable<RecurringPaymentInstruction | null | undefined> {
    return combineLatest([previousActive$, newScheduled$]).pipe(
      map(([active, newScheduled]) => {
        if (!active || !newScheduled) return active;

        if (VbPaymentChangeReviewStepUtils.sameRecurringAmount(newScheduled, active)) return active;

        const clone = { ...active } as RecurringPaymentInstruction;
        clone.endDate =
          frequency === 'monthly'
            ? moment.utc(newScheduled.paymentStartDate).subtract(1, 'month').format('YYYY-MM-DD')
            : moment.utc(newScheduled.paymentStartDate).subtract(1, 'year').format('YYYY-MM-DD');
        return clone;
      })
    );
  }

  static buildVbPaymentChangeRequest(
    lumpSumInstruction: LumpSumPaymentInstruction | null,
    forgoRecurring: boolean,
    withdrawalInstructions: WithdrawalInstruction[],
    bankAccountId: number | null,
    annualMinDetails: AnnualMinimumDetails,
    monthlyForm: VbPaymentChangeRecurringPaymentFormGroup | null,
    annualForm: VbPaymentChangeRecurringPaymentFormGroup | null,
    vbAccountDetails: VariableBenefitAccountDetails,
    estimate: MemberVbAccountEstimatedAnnualPaymentTotal
  ): CreateVariableBenefitPaymentChangeRequest {
    const req = new CreateVariableBenefitPaymentChangeRequest();
    req.minimumAgeSpouseId = annualMinDetails.minimumAgeSpouseId;
    req.minimumAgeDateOfBirth = annualMinDetails.minimumAgeDateOfBirth;
    req.forgoRecurringPayments = forgoRecurring;
    // The submission API uses -1 to represent a written cheque. Keep the UI state nullable,
    // but do not send null because the API cannot process it.
    req.bankAccountId = bankAccountId ?? -1;
    req.paymentMethodId = req.bankAccountId === -1 ? PaymentMethodTypeEnum.Cheque : PaymentMethodTypeEnum.Eft;

    if (lumpSumInstruction && !forgoRecurring) {
      req.lumpSumPaymentInstructionChange = new LumpSumPaymentInstructionChangeRequest(
        lumpSumInstruction.desiredPaymentDate,
        lumpSumInstruction.paymentAmount
      );
    }

    if (monthlyForm?.dirty) {
      req.monthlyRecurringPaymentInstructionChange = VbPaymentChangeReviewStepUtils.recurringPaymentChangeFromForm(
        monthlyForm,
        estimate.monthlyMinimumPaymentAmount
      );
    } else if (monthlyForm === null && vbAccountDetails.scheduledMonthlyRecurringPaymentInstruction !== null) {
      const scheduled = vbAccountDetails.scheduledMonthlyRecurringPaymentInstruction;
      const active = vbAccountDetails.activeMonthlyRecurringPaymentInstruction;
      req.monthlyRecurringPaymentInstructionChange = new RecurringPaymentInstructionChangeRequest(
        scheduled.paymentStartDate.substring(0, 10),
        active?.paymentAmount ?? 0,
        active?.useMinimumAmount ?? false
      );
    }

    if (annualForm?.dirty) {
      req.annualRecurringPaymentInstructionChange = VbPaymentChangeReviewStepUtils.recurringPaymentChangeFromForm(
        annualForm,
        estimate.annualMinimumPaymentAmount
      );
    } else if (annualForm === null && vbAccountDetails.scheduledAnnualRecurringPaymentInstruction !== null) {
      const scheduled = vbAccountDetails.scheduledAnnualRecurringPaymentInstruction;
      const active = vbAccountDetails.activeAnnualRecurringPaymentInstruction;
      req.annualRecurringPaymentInstructionChange = new RecurringPaymentInstructionChangeRequest(
        scheduled.paymentStartDate.substring(0, 10),
        active?.paymentAmount ?? 0,
        active?.useMinimumAmount ?? false
      );
    }

    req.withdrawalInstructions = withdrawalInstructions.map(
      wi => new CreateWithdrawalInstructionRequest(wi.fundId, wi.percentage)
    );

    if (forgoRecurring) {
      req.annualRecurringPaymentInstructionChange = null;
      req.monthlyRecurringPaymentInstructionChange = null;
      req.lumpSumPaymentInstructionChange = null;
    }

    return req;
  }

  private static scheduledRecurringChangeNotice(
    change: RecurringPaymentInstructionChangeRequest | null,
    active: RecurringPaymentInstruction | null | undefined,
    scheduled: RecurringPaymentInstruction | null | undefined,
    newlyForgone: boolean
  ): VbScheduledChangeNotice | null {
    if (!scheduled?.paymentStartDate) return null;

    const scheduledDate = scheduled.paymentStartDate.substring(0, 10);
    if (newlyForgone) return { outcome: VbScheduledChangeOutcomeEnum.Replaces, scheduledDate };
    if (!change) return null;

    const equalsCurrent = active
      ? VbPaymentChangeReviewStepUtils.sameRecurringAmount(change, active)
      : !change.useMinimumAmount && change.paymentAmount === 0;
    if (equalsCurrent) return { outcome: VbScheduledChangeOutcomeEnum.Cancels, scheduledDate };

    const sameStart = change.paymentStartDate.substring(0, 10) === scheduledDate;
    if (sameStart && VbPaymentChangeReviewStepUtils.sameRecurringAmount(change, scheduled)) return null;

    return { outcome: VbScheduledChangeOutcomeEnum.Replaces, scheduledDate };
  }

  private static scheduledLumpSumNotice(
    change: LumpSumPaymentInstructionChangeRequest | null,
    waiting: LumpSumPaymentInstruction | null | undefined,
    newlyForgone: boolean
  ): VbScheduledChangeNotice | null {
    if (!waiting?.desiredPaymentDate) return null;

    const scheduledDate = waiting.desiredPaymentDate.substring(0, 10);
    if (newlyForgone) return { outcome: VbScheduledChangeOutcomeEnum.Replaces, scheduledDate };
    if (!change) return null;
    if (change.paymentAmount === 0) return { outcome: VbScheduledChangeOutcomeEnum.Cancels, scheduledDate };

    const sameMonth = change.desiredPaymentDate.substring(0, 7) === scheduledDate.substring(0, 7);
    if (sameMonth && change.paymentAmount === waiting.paymentAmount) return null;

    return { outcome: VbScheduledChangeOutcomeEnum.Replaces, scheduledDate };
  }

  private static recurringPaymentChangeFromForm(
    form: VbPaymentChangeRecurringPaymentFormGroup,
    minimumPaymentAmount: number | null
  ): RecurringPaymentInstructionChangeRequest | null {
    const change = VbPaymentChangePaymentsStepUtils.toRecurringPaymentChange(form) ?? null;
    if (!change?.useMinimumAmount || minimumPaymentAmount === null) return change;
    return new RecurringPaymentInstructionChangeRequest(change.paymentStartDate, minimumPaymentAmount, true);
  }
}
